import { Box, Button, IconButton, Switch, TextField, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { FOOTER, HEADER, STORY_CONTENT_CARD } from '@opengov/stories-shared/constants';
import { INewStoryContent, IStory, IStoryContent } from '@opengov/stories-shared/types';
import { selectNewStoryContents, selectStoryContentFormats } from '@opengov/stories-shared/utils/storyContents';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DragSource, DropTarget } from 'react-dnd';

import { IStoryViewMessages, IUpdateStoryContentProps } from '../../../../types';
import { CLEAR_CONTENT, RESET_CONTENT } from '../../../constants';
import { copyContent, updateLogo } from '../../../utils';

import { styles } from './StoryContentCard.styles';

export type IStoryContentCardProps = {
  key: string;
  entityName: string;
  storyContentInView: string;
  storyContent: IStoryContent;
  isTemplate: boolean;
  firstId: string;
  lastId: string;
  story: IStory;
  canDrag: boolean;
  messages: IStoryViewMessages;
  isDragging?: boolean;
  updateStoryContentDB(props: IUpdateStoryContentProps): Promise<void>;
  moveStoryContent(storyContentId: string, atIndex: number): void;
  findStoryContent(storyContentId: string): {
    storyContent: IStoryContent;
    index: number;
  };
  persistStoryContentOrder?(): void;
  connectDragSource?(arg): JSX.Element;
  connectDropTarget?(arg): JSX.Element;
  connectDragPreview?(arg): JSX.Element;
  updateStoryContent(storyContent: IStoryContent | INewStoryContent): Promise<void>;
  updateMetaStoryContent(storyContent: IStoryContent): Promise<void>;
  openModal(modal: string): IAction<string, string>;
  onClearContent(storyContent: IStoryContent): void;
  onResetContent(storyContent: IStoryContent): void;
};

const cardSource = {
  canDrag: (props: IStoryContentCardProps): boolean => props.canDrag,

  beginDrag(props: IStoryContentCardProps): { id: string; originalIndex: number } {
    const {
      storyContent: { _id },
      findStoryContent
    } = props;
    return {
      id: _id,
      originalIndex: findStoryContent(props.storyContent._id).index
    };
  },

  endDrag(props: IStoryContentCardProps, monitor) {
    const { moveStoryContent, persistStoryContentOrder } = props;
    const { id: droppedId, originalIndex } = monitor.getItem();
    const didDrop = monitor.didDrop();
    if (!didDrop) {
      moveStoryContent(droppedId, originalIndex);
    } else {
      persistStoryContentOrder?.();
    }
  }
};

const cardTarget = {
  canDrop(): boolean {
    return false;
  },

  hover(props: IStoryContentCardProps, monitor: any) {
    const { id: draggedId } = monitor.getItem();
    const {
      storyContent: { _id: overId },
      firstId,
      lastId,
      moveStoryContent,
      findStoryContent
    } = props;

    if (overId !== firstId && overId !== lastId && draggedId !== overId) {
      const { index: overIndex } = findStoryContent(overId);
      moveStoryContent(draggedId, overIndex);
    }
  }
};

function StoryContentCardBase(props: IStoryContentCardProps) {
  const {
    storyContent,
    entityName,
    isTemplate,
    story,
    storyContentInView,
    canDrag,
    isDragging,
    connectDragSource,
    connectDropTarget,
    connectDragPreview,
    messages,
    updateStoryContent,
    updateStoryContentDB,
    onClearContent,
    onResetContent
  } = props;

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(storyContent.title);
  const [active, setActive] = useState(storyContent.active);

  useEffect(() => {
    setTitle(storyContent.title);
  }, [storyContent.title]);

  useEffect(() => {
    setActive(storyContent.active);
  }, [storyContent.active]);

  const handleUpdateStoryContentImpl = useCallback(
    (sc: IStoryContent | INewStoryContent) => {
      void updateStoryContent(sc);
      void updateStoryContentDB({ storyContent: sc, isTemplate, story });
    },
    [updateStoryContent, updateStoryContentDB, isTemplate, story]
  );

  const handleUpdateStoryContent = useMemo(
    () => debounce(handleUpdateStoryContentImpl, 500),
    [handleUpdateStoryContentImpl]
  );

  const toggleOpen = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  }, []);

  const toggleSectionActive = useCallback(
    (checked: boolean) => {
      setActive(checked);
      handleUpdateStoryContent({ ...storyContent, active: checked });
    },
    [handleUpdateStoryContent, storyContent]
  );

  const updateFormat = useCallback(
    (format: string) => {
      const { _id, active: contentActive, type } = storyContent;
      const newStoryContents = selectNewStoryContents();
      const newStoryContent = newStoryContents.find((m) => m.format === format);
      if (!newStoryContent) return;

      const newContent = copyContent(storyContent, newStoryContent as IStoryContent);
      const content =
        type === HEADER && newContent.indexOf('LOGOTYPE') > -1 && entityName
          ? updateLogo(newContent, entityName.toUpperCase())
          : newContent;

      if (storyContent?.order) {
        /*
        reassign the old order of story content if present
        so order of section is not changed
        */
        newStoryContent.order = storyContent.order;
      }
      // Start from the persisted section so identity fields the template lacks
      // (author, entity_id, story) survive; the DB save reads them.
      handleUpdateStoryContent({
        ...storyContent,
        ...newStoryContent,
        _id,
        title,
        active: contentActive,
        content
      });
    },
    [storyContent, entityName, title, handleUpdateStoryContent]
  );

  const updateModal = useCallback(
    (modal: string) => {
      if (modal === CLEAR_CONTENT) {
        onClearContent(storyContent);
      } else if (modal === RESET_CONTENT) {
        onResetContent(storyContent);
      }
    },
    [storyContent, onClearContent, onResetContent]
  );

  const handleTitle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextTitle = event.target.value;
      event.preventDefault();
      setTitle(nextTitle);
      handleUpdateStoryContent({
        ...storyContent,
        title: nextTitle
      });
    },
    [storyContent, handleUpdateStoryContent]
  );

  const handleScrollTo = useCallback(() => {
    if (!storyContent.scrollTo) {
      void updateStoryContent({
        ...storyContent,
        scrollTo: true
      });
    }
  }, [storyContent, updateStoryContent]);

  const handleCardKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleScrollTo();
      }
    },
    [handleScrollTo]
  );

  const { _id, type, format } = storyContent;
  const canToggle = type === HEADER || type === FOOTER;
  const storyContentArray = selectStoryContentFormats(storyContent);
  const focused = storyContentInView === _id;

  return connectDragPreview!(
    connectDropTarget!(
      <div>
        <Box
          tabIndex={0}
          sx={[
            isDragging ? styles.draggingHidden : styles.draggingVisible,
            styles.card,
            open ? styles.cardOpen : {},
            focused && !open ? styles.cardFocused : {}
          ]}
          onClick={handleScrollTo}
          onKeyDown={handleCardKeyDown}
        >
          <Box sx={styles.titleWrapper}>
            <TextField
              variant="standard"
              value={title}
              onChange={handleTitle}
              InputProps={{ disableUnderline: true }}
              inputProps={{
                'aria-label': messages.clickToEditTitle,
                className: 'no-format',
                'data-hint': messages.clickToEditTitle,
                'data-hint-position': 'bottom',
                'data-hint-align': 'center'
              }}
              sx={styles.titleInner}
            />
          </Box>
          <Box sx={styles.icons}>
            {canDrag &&
              connectDragSource!(
                <div
                  data-hint={messages.dragToReorder}
                  data-hint-position="bottom"
                  data-hint-align="center"
                >
                  <DragIndicatorIcon sx={styles.iconColor} fontSize="small" />
                </div>
              )}
            {canToggle && (
              <Switch
                checked={active}
                size="small"
                id={`toggle-${_id}`}
                inputProps={{ 'aria-label': 'toggle section active' }}
                onChange={(e) => toggleSectionActive(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                sx={styles.switch}
              />
            )}
            <Tooltip
              title={open ? messages.showLessOptions : messages.showMoreOptions}
              placement="bottom"
            >
              <IconButton
                size="small"
                onClick={toggleOpen}
                sx={styles.iconColor}
                aria-label={open ? messages.showLessOptions : messages.showMoreOptions}
              >
                {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {open && (
          <Box sx={styles.details}>
            {storyContentArray.map((m, idx) => (
              <Box
                key={`${m.format}-${idx}`}
                sx={[styles.formatContainer, format === m.format ? styles.formatActive : {}]}
              >
                <IconButton
                  sx={styles.thumbnailButton}
                  onClick={() => updateFormat(m.format)}
                  aria-label={m.format}
                >
                  <Box component="img" sx={styles.thumbnail} src={m.thumbnail} alt="" />
                </IconButton>
              </Box>
            ))}
            <Box sx={styles.buttonsRow}>
              <Button variant="outlined" size="small" onClick={() => updateModal(RESET_CONTENT)}>
                {messages.resetContent}
              </Button>
              <Button variant="outlined" size="small" onClick={() => updateModal(CLEAR_CONTENT)}>
                {messages.clearContent}
              </Button>
            </Box>
          </Box>
        )}
      </div>
    )
  );
}

export const StoryContentCard = DropTarget(STORY_CONTENT_CARD, cardTarget, (connect) => ({
  connectDropTarget: connect.dropTarget()
}))(
  DragSource(STORY_CONTENT_CARD, cardSource, (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging()
  }))(StoryContentCardBase)
);
